/**
 * Reusable quiz engine — manages questions, timer, progress, and results.
 * Renders each question component and validates answers.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/Button";

export interface QuizQuestion {
  id: string;
  title: string;
  instruction: string;
  timeLimit: number; // seconds
  /** Render the interactive question UI. Call onComplete(true) when answered correctly. */
  render: (props: { onComplete: (correct: boolean) => void }) => React.ReactNode;
}

interface QuizEngineProps {
  title: string;
  questions: QuizQuestion[];
  onPass: () => void;
  onClose: () => void;
}

type QuestionResult = { correct: boolean; timeUsed: number } | null;

export function QuizEngine({ questions, onPass, onClose }: QuizEngineProps) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [results, setResults] = useState<QuestionResult[]>(() => questions.map(() => null));
  const [timeLeft, setTimeLeft] = useState(questions[0]?.timeLimit ?? 30);
  const [phase, setPhase] = useState<"intro" | "question" | "feedback" | "results">("intro");
  const [lastCorrect, setLastCorrect] = useState(false);
  const startTimeRef = useRef(Date.now());

  const question = questions[currentIdx];

  // Timer countdown
  useEffect(() => {
    if (phase !== "question") return;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          // Time's up — mark as incorrect
          handleAnswer(false);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, currentIdx]);

  const startQuestion = useCallback(() => {
    setTimeLeft(question.timeLimit);
    startTimeRef.current = Date.now();
    setPhase("question");
  }, [question]);

  const handleAnswer = useCallback(
    (correct: boolean) => {
      if (phase !== "question") return;
      const timeUsed = Math.round((Date.now() - startTimeRef.current) / 1000);
      setResults((prev) => {
        const next = [...prev];
        next[currentIdx] = { correct, timeUsed };
        return next;
      });
      setLastCorrect(correct);
      setPhase("feedback");
    },
    [phase, currentIdx],
  );

  const nextQuestion = useCallback(() => {
    if (currentIdx + 1 >= questions.length) {
      setPhase("results");
    } else {
      setCurrentIdx((i) => i + 1);
      setPhase("intro");
    }
  }, [currentIdx, questions.length]);

  const allCorrect = results.every((r) => r?.correct);
  const correctCount = results.filter((r) => r?.correct).length;

  // Intro screen before each question
  if (phase === "intro") {
    return (
      <div style={overlay}>
        <div style={modal}>
          <div style={{ fontSize: 10, color: "var(--c-accent)", textTransform: "uppercase", letterSpacing: 2, fontFamily: "var(--font-mono)", marginBottom: 8 }}>
            Question {currentIdx + 1} of {questions.length}
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: "0 0 12px", color: "var(--c-text)" }}>
            {question.title}
          </h2>
          <p style={{ fontSize: 14, color: "var(--c-text-muted)", lineHeight: 1.6, marginBottom: 24 }}>
            {question.instruction}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <div style={{ padding: "4px 10px", background: "rgba(234,179,8,0.15)", border: "1px solid rgba(234,179,8,0.3)", fontSize: 12, fontWeight: 700, color: "#eab308", fontFamily: "var(--font-mono)" }}>
              {question.timeLimit}s
            </div>
            <span style={{ fontSize: 12, color: "var(--c-text-dim)" }}>Time limit</span>
          </div>
          <Button onClick={startQuestion}>Start</Button>
        </div>
      </div>
    );
  }

  // Active question
  if (phase === "question") {
    const urgency = timeLeft <= 5 ? "#ef4444" : timeLeft <= 10 ? "#eab308" : "var(--c-text-muted)";
    return (
      <div style={{ position: "relative", width: "100%", minHeight: "70vh" }}>
        {/* Timer + instruction bar */}
        <div style={{
          position: "sticky", top: 0, zIndex: 50, display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "8px 16px", background: "var(--c-surface)", borderBottom: "1px solid var(--c-border)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 10, color: "var(--c-accent)", textTransform: "uppercase", fontFamily: "var(--font-mono)", fontWeight: 700 }}>
              Q{currentIdx + 1}/{questions.length}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-text)" }}>{question.instruction}</span>
          </div>
          <div style={{
            padding: "4px 12px", fontSize: 16, fontWeight: 800, fontFamily: "var(--font-mono)",
            color: urgency, minWidth: 50, textAlign: "center",
          }}>
            {timeLeft}s
          </div>
        </div>

        {/* Question content */}
        <div style={{ padding: "16px 0" }}>
          {question.render({ onComplete: handleAnswer })}
        </div>
      </div>
    );
  }

  // Feedback after answering
  if (phase === "feedback") {
    return (
      <div style={overlay}>
        <div style={modal}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%", margin: "0 auto 16px",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, fontWeight: 800,
            background: lastCorrect ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
            color: lastCorrect ? "#22c55e" : "#ef4444",
            border: `2px solid ${lastCorrect ? "#22c55e" : "#ef4444"}`,
          }}>
            {lastCorrect ? "\u2713" : "\u2717"}
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "var(--c-text)", textAlign: "center", margin: "0 0 8px" }}>
            {lastCorrect ? "Correct!" : "Incorrect"}
          </h2>
          <p style={{ fontSize: 13, color: "var(--c-text-muted)", textAlign: "center", marginBottom: 24 }}>
            {lastCorrect
              ? `Completed in ${results[currentIdx]?.timeUsed ?? 0}s`
              : timeLeft <= 0 ? "Time's up!" : "That wasn't quite right."}
          </p>
          <Button onClick={nextQuestion}>
            {currentIdx + 1 >= questions.length ? "See Results" : "Next Question"}
          </Button>
        </div>
      </div>
    );
  }

  // Final results
  return (
    <div style={overlay}>
      <div style={{ ...modal, maxWidth: 500 }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: allCorrect ? "#22c55e" : "var(--c-text)", textAlign: "center", margin: "0 0 8px" }}>
          {allCorrect ? "Quiz Passed!" : "Quiz Failed"}
        </h2>
        <p style={{ fontSize: 14, color: "var(--c-text-muted)", textAlign: "center", marginBottom: 24 }}>
          {correctCount}/{questions.length} correct
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
          {questions.map((q, i) => {
            const r = results[i];
            return (
              <div key={q.id} style={{
                display: "flex", alignItems: "center", gap: 12, padding: "8px 12px",
                background: "var(--c-surface-2)", border: "1px solid var(--c-border)",
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700,
                  background: r?.correct ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                  color: r?.correct ? "#22c55e" : "#ef4444",
                }}>
                  {r?.correct ? "\u2713" : "\u2717"}
                </div>
                <span style={{ flex: 1, fontSize: 13, color: "var(--c-text)" }}>{q.title}</span>
                {r && <span style={{ fontSize: 11, color: "var(--c-text-dim)", fontFamily: "var(--font-mono)" }}>{r.timeUsed}s</span>}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          {allCorrect ? (
            <Button onClick={onPass}>Claim Certificate</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>Back to Training</Button>
              <Button onClick={() => {
                setCurrentIdx(0);
                setResults(questions.map(() => null));
                setPhase("intro");
              }}>
                Retry Quiz
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  display: "flex", justifyContent: "center", alignItems: "center",
  minHeight: "60vh", padding: 24,
};

const modal: React.CSSProperties = {
  background: "var(--c-surface)", border: "1px solid var(--c-border)",
  padding: 32, maxWidth: 440, width: "100%", textAlign: "center",
};
